import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Card — the base "lifted floating card" surface.
 *
 * Bento language: rounded 20px, hairline border, soft shadow that
 * lifts the card off the body's gradient bg. The default radius is
 * deliberately bigger than a typical SaaS card — the dashboard's
 * grid relies on these big corners reading as cohesive against the
 * sidebar's navigation pills.
 *
 * Variants:
 *   • default        — base bento card (rounded-bento, soft shadow)
 *   • flat           — same surface but no shadow + tighter radius;
 *                      use this for nested sub-panels inside a card.
 *   • interactive    — adds hover lift (shadow grows, no transform —
 *                      we don't want pages full of dancing cards).
 *
 * The ``padding`` prop stays for backwards compat — old code passes
 * ``padding={false}`` for tables that need to span the whole card.
 */
interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  padding?: boolean;
  variant?: "default" | "flat" | "interactive";
}

export function Card({
  children,
  padding = true,
  variant = "default",
  className,
  ...props
}: CardProps) {
  return (
    <div
      className={cn(
        "bg-surface border border-border",
        variant === "flat"
          ? "rounded-bentoSm"
          : "rounded-bento shadow-bento",
        variant === "interactive" &&
          "transition-shadow duration-200 hover:shadow-bentoHover",
        padding && "p-6",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center justify-between mb-4", className)}>
      {children}
    </div>
  );
}

export function CardTitle({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <h3
      className={cn(
        "text-lg font-semibold font-heading text-text-primary",
        className,
      )}
    >
      {children}
    </h3>
  );
}
